import { useTranslation } from "react-i18next";

import { validateAuthToken } from "@/guards/validateAuthToken";
import { validateActiveUser } from "@/guards/validateActiveUser";

import { SectionRoute } from "@/components/shared/SectionRoute";
import { StateMessageFactory } from "@/components/shared/state-messages/StateMessageFactory";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { getItemSessionStorage } from "@/lib/utils";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { getPatientById } from "@/app/store/patient/patientSlice";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/dashboard/patients/$id")({
  beforeLoad: () => {
    validateAuthToken();
    validateActiveUser();
  },
  component: PatientLayout,
});

function PatientLayout() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const dispatch = useAppDispatch();

  const selectedPatientId = useAppSelector(
    (state) => state.connection.selectedConnectionId,
  );

  const persistedPatientId = getItemSessionStorage(ELocalStorageKeys.PATIENT);
  const patientId = selectedPatientId || persistedPatientId;

  const { data, status } = useAppSelector((state) => state.patient);

  const location = useLocation();

  const isMedicationRoute = location.pathname.includes("/medications/");
  const patientName = data?.patient?.firstname
    ? `${data.patient.firstname} ${data.patient.surnames}`
    : "";
  const isPatientNameLoading =
    !patientName && (status === "idle" || status === "loading");

  useEffect(() => {
    if (!patientId) return;

    dispatch(getPatientById(patientId));
  }, [dispatch, patientId]);

  if (!patientId || patientId.slice(-6) !== id.slice(-6)) {
    return (
      <div className="mt-12 2xl:mt-24 mx-auto xl:max-w-4/5 2xl:max-w-3/5">
        {StateMessageFactory("patient-not-found")}
      </div>
    );
  }

  return (
    <>
      <SectionRoute
        route_one={t("routes.dashboard")}
        route_one_link="/dashboard"
        route_two={t("routes.patients")}
        route_two_link="/dashboard/patients"
        route_three={
          isPatientNameLoading ? (
            <Skeleton className="h-5 w-40 rounded-md" />
          ) : (
            patientName
          )
        }
        route_four={isMedicationRoute ? t("routes.medication") : undefined}
      />

      <Outlet />
    </>
  );
}
